import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  listInventoryItems,
  useBulkUpdateInventoryItemStock,
  getListInventoryItemsQueryKey,
  type InventoryItem,
  type BulkUpdateInventoryItemStockResult,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, Download, FileSpreadsheet, Info, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// Bulk Stock Update — operator-driven stock-take reconciliation.
// Operator exports the current items list as CSV (with the current on-hand
// number pre-filled), physically counts, edits the "new_current_stock"
// column with what's actually on the shelf, and re-uploads. The server
// applies the values as ABSOLUTE numbers (not deltas) in a single
// transaction. Useful when stock-in receipts are already correct on
// record and only the on-hand running total needs fixing — no new
// stock-in events are created.

const EXPORT_HEADERS = [
  "item_code",
  "name",
  "brand",
  "unit_of_measurement",
  "current_stock_now",
  "new_current_stock",
];

function escapeCsvField(v: string | number | null | undefined): string {
  const s = v == null ? "" : String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadFile(filename: string, content: string, mime = "text/csv") {
  // BOM prefix so Excel opens UTF-8 CSV correctly (otherwise non-ASCII
  // names — Hindi, ₹ etc. — render as garbage).
  const blob = new Blob(["﻿" + content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Minimal RFC4180-ish CSV parser. Handles quoted fields, escaped quotes,
// and CRLF / LF line endings. We don't pull a library because the rest of
// the codebase parses CSVs by hand and this avoids a new dep for ~30 lines.
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ",") {
        row.push(field);
        field = "";
      } else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && text[i + 1] === "\n") i++;
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      } else {
        field += ch;
      }
    }
  }
  // Trailing field / row without newline.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Drop fully-empty trailing rows.
  while (rows.length > 0 && rows[rows.length - 1]!.every((c) => c === "")) {
    rows.pop();
  }
  return rows;
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function BulkStockUpdateDialog({ open, onOpenChange }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);
  const [lastResult, setLastResult] = useState<BulkUpdateInventoryItemStockResult | null>(null);
  const [lastFilename, setLastFilename] = useState<string | null>(null);

  const uploadMutation = useBulkUpdateInventoryItemStock({
    mutation: {
      onSuccess: (result) => {
        setLastResult(result);
        toast({
          title: "Stock update processed",
          description: `${result.succeededRows} of ${result.totalRows} rows applied${
            result.failedRows > 0 ? ` (${result.failedRows} failed)` : ""
          }`,
        });
        // Invalidate the items query so the new on-hand numbers show
        // up in the Items tab without a hard refresh.
        void qc.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : "Upload failed";
        toast({ title: "Upload failed", description: msg, variant: "destructive" });
      },
    },
  });

  const onExport = async () => {
    setExporting(true);
    try {
      // listInventoryItems already returns the full space-scoped item set
      // (no pagination on this endpoint), so no params needed.
      const items: InventoryItem[] = (await listInventoryItems()) as unknown as InventoryItem[];
      const lines: string[] = [EXPORT_HEADERS.map(escapeCsvField).join(",")];
      for (const it of items) {
        // Skip child variants — they get their own current_stock row on
        // the parent's variant list and aren't safe to bulk-edit by
        // item_code (their codes are derived). Operator can still adjust
        // them through the parent form.
        const isChild = (it as unknown as { parentItemId?: number | null }).parentItemId != null;
        if (isChild) continue;
        lines.push(
          [
            it.itemCode ?? "",
            it.name ?? "",
            (it as unknown as { brand?: string | null }).brand ?? "",
            it.unitOfMeasurement ?? "",
            String(it.currentStock ?? "0"),
            // Pre-fill new = current so unchanged rows are no-ops.
            // Operator overwrites only the rows that need fixing.
            String(it.currentStock ?? "0"),
          ]
            .map(escapeCsvField)
            .join(","),
        );
      }
      const stamp = new Date().toISOString().slice(0, 10);
      downloadFile(`inventory-stock-${stamp}.csv`, lines.join("\n"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not export";
      toast({ title: "Export failed", description: msg, variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 5 MB", variant: "destructive" });
      return;
    }
    const text = await file.text();
    const rows = parseCsv(text);
    if (rows.length < 2) {
      toast({ title: "Empty CSV", description: "No data rows found.", variant: "destructive" });
      return;
    }
    const headerRow = rows[0]!.map((h) => h.trim().toLowerCase());
    const codeIdx = headerRow.indexOf("item_code");
    const newIdx = headerRow.indexOf("new_current_stock");
    if (codeIdx === -1 || newIdx === -1) {
      toast({
        title: "Missing columns",
        description: "CSV must include item_code and new_current_stock columns. Re-download the template.",
        variant: "destructive",
      });
      return;
    }

    const updates: { itemCode: string; currentStock: number }[] = [];
    const parseErrors: string[] = [];
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i]!;
      const code = (r[codeIdx] ?? "").trim();
      const raw = (r[newIdx] ?? "").trim();
      if (!code) continue;        // skip blank-coded rows silently
      if (raw === "") continue;   // skip rows the operator left empty
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) {
        parseErrors.push(`Row ${i + 1} (${code}): "${raw}" is not a valid stock count`);
        continue;
      }
      updates.push({ itemCode: code, currentStock: n });
    }

    if (updates.length === 0) {
      toast({
        title: "Nothing to update",
        description:
          parseErrors.length > 0
            ? parseErrors.slice(0, 3).join("; ")
            : "No rows had a value in the new_current_stock column.",
        variant: "destructive",
      });
      return;
    }

    setLastFilename(file.name);
    uploadMutation.mutate({ data: { updates } });
  };

  const downloadErrorReport = () => {
    if (!lastResult || lastResult.errors.length === 0) return;
    const lines = [
      "row,item_code,message",
      ...lastResult.errors.map(
        (e) =>
          `${e.row},${escapeCsvField(e.itemCode ?? "")},${escapeCsvField(e.message)}`,
      ),
    ];
    const base = (lastFilename ?? "bulk-stock-update").replace(/\.csv$/i, "");
    downloadFile(`${base}-errors.csv`, lines.join("\n"));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          // Wipe transient state on close so the next open is a fresh run.
          setLastResult(null);
          setLastFilename(null);
        }
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" /> Bulk Stock Update
          </DialogTitle>
          <DialogDescription>
            Update the current on-hand quantity for many items at once. Use this for
            stock-take reconciliation when the items have already been stocked in
            and only the on-hand count needs correcting.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-sm space-y-1">
            <div>
              <strong>How it works:</strong> Download the current stock as a CSV
              (opens directly in Excel), edit the <code>new_current_stock</code>{" "}
              column with the actual on-hand quantity, then upload it back.
            </div>
            <div>
              Values are <strong>absolute</strong> — what you write is what gets
              saved. Blank <code>new_current_stock</code> rows are skipped. No
              stock-in receipt is created.
            </div>
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium mb-1.5">Step 1 — Export current stock</p>
            <Button variant="outline" size="sm" onClick={onExport} disabled={exporting}>
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {exporting ? "Preparing…" : "Download stock CSV"}
            </Button>
          </div>

          <div>
            <p className="text-sm font-medium mb-1.5">Step 2 — Upload the edited CSV</p>
            <Button size="sm" onClick={onPickFile} disabled={uploadMutation.isPending}>
              {uploadMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              {uploadMutation.isPending ? "Applying…" : "Upload CSV"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={onFileChange}
              data-testid="input-bulk-stock-csv"
            />
          </div>
        </div>

        {lastResult && (
          <div className="rounded-md border p-3 space-y-2">
            <p className="text-sm font-medium">Last run</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <span>
                Total: <span className="tabular-nums">{lastResult.totalRows}</span>
              </span>
              <span className="text-green-700">
                Succeeded: <span className="tabular-nums">{lastResult.succeededRows}</span>
              </span>
              <span className={lastResult.failedRows > 0 ? "text-red-700" : "text-muted-foreground"}>
                Failed: <span className="tabular-nums">{lastResult.failedRows}</span>
              </span>
            </div>
            {lastResult.failedRows > 0 && (
              <>
                <ul className="text-xs space-y-0.5 max-h-32 overflow-y-auto pl-4 list-disc text-muted-foreground">
                  {lastResult.errors.slice(0, 10).map((e, idx) => (
                    <li key={idx}>
                      <span className="font-mono">{e.itemCode || `row ${e.row}`}</span>: {e.message}
                    </li>
                  ))}
                  {lastResult.errors.length > 10 && (
                    <li className="italic">…and {lastResult.errors.length - 10} more</li>
                  )}
                </ul>
                <Button variant="ghost" size="sm" onClick={downloadErrorReport}>
                  <Download className="mr-2 h-3 w-3" /> Download error report
                </Button>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
